import { useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { TextField } from '@mui/material';

import { ConfirmationDialog } from 'components/surfaces/dialogs/ConfirmationDialog';

export type CreateProposalDialogProps = {
  onConfirm: (name: string) => void;
  onClose: () => void;
  isLoading: boolean;
};

export const CreateProposalDialog: React.FC<CreateProposalDialogProps> = ({ onConfirm, onClose, isLoading }) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isValid }
  } = useForm<{ name: string }>({
    defaultValues: {
      name: ''
    },
    mode: 'onChange'
  });

  const disabled = !isDirty || !isValid || isLoading;

  const onSubmit = useCallback(
    // No reset here: on success the parent unmounts this dialog, and on failure the typed name must survive.
    (data: { name: string }) => onConfirm(data.name),
    [onConfirm]
  );

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  return (
    <ConfirmationDialog
      title='Create new proposal'
      id='create-proposal-dialog'
      onClose={handleClose}
      onConfirm={handleSubmit(onSubmit)}
      maxWidth='sm'
      fullWidth
      loading={isLoading}
      confirmButtonText='Create'
      cancelButtonText='Cancel'
      open
      disabled={disabled}
    >
      <form
        onSubmit={(event) => {
          if (disabled) {
            event.preventDefault();
            return;
          }
          handleSubmit(onSubmit)(event);
        }}
      >
        <Controller
          name='name'
          control={control}
          rules={{
            required: 'Proposal name cannot be empty',
            minLength: { value: 3, message: 'Proposal name must be at least 3 characters' },
            maxLength: { value: 50, message: 'Proposal name must be under 50 characters' }
          }}
          render={({ field }) => (
            <TextField
              slotProps={{
                htmlInput: {
                  autoComplete: 'off'
                }
              }}
              id='proposal-name'
              data-testid='proposal-name-textfield'
              label='Name'
              autoFocus
              fullWidth
              error={Boolean(errors.name)}
              helperText={errors.name?.message}
              {...field}
            />
          )}
        />
      </form>
    </ConfirmationDialog>
  );
};
